import { useApolloClient } from '@apollo/client';
import { Col, Row } from 'antd';
import classNames from 'classnames';
import { every, isNumber } from 'lodash-es';
import { useCallback, useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import { DIMENSIONAL_WEIGHT_QUERY } from '../../../app/graphql/jobQueries';
import { extractGraphqlEntity } from '../../../common/utils/graphqlUtils';
import { SettingsContext } from '../../../components/domainSpecific/settingsElements';
import { useDynamicOriginalValueDependency } from '../../../components/forms/dynamic/dynamicFormDependencies';
import { ConfigurableCard } from '../../../components/layout/cardElements';
import {
  TwoLines,
  TwoLinesSeparator,
} from '../../../components/layout/layoutElements';
import { useLayoutElementsStyles } from '../../../components/layout/layoutElements.styles';
import { useResponsiveQueries } from '../../../components/responsive/responsiveQueries';
import { cssVariables } from '../../../styles/cssVariables';
import { pxToNumber } from '../../../utils/cssUtils';
import { withMergedClass } from '../../../utils/reactUtils';
import { usePackageListCommonStyles } from './packageListCommon.styles';

export function usePackageListResponsiveQueries() {
  const media = useResponsiveQueries();
  const isSm = media.xs || media.sm || media.md;
  const isLg = media.lg || media.xl || media.xxl;

  return { isSm, isLg, media };
}

export function useServiceInformationDeps() {
  const commodity = useDynamicOriginalValueDependency(
    'serviceInformation.commodity'
  );

  return { commodity };
}

export const DividerCol = ({ className, smallVerticalSpace }) => {
  const { styles: layoutStyles } = useLayoutElementsStyles();
  return (
    <Col
      style={{
        alignSelf: 'stretch',
        margin: `${smallVerticalSpace ? '5px' : '0'} -0.5px`,
        paddingLeft: 0,
        paddingRight: 0,
      }}
    >
      <div
        className={classNames(layoutStyles.colVerticalDividerFull, className)}
      />
    </Col>
  );
};

export const SymbolCol = withMergedClass('SymbolCol')(Col);

export function PackageCard({ index, children, headerExtra, className }) {
  const { styles } = usePackageListCommonStyles();
  return (
    <ConfigurableCard
      title={
        isNumber(index) ? (
          <FormattedMessage
            id="book.newShipment.packageItemHeaderWithNumber"
            values={{ index: index + 1 }}
          />
        ) : (
          <FormattedMessage id="book.newShipment.packageItemHeader" />
        )
      }
      headerExtra={headerExtra}
      bodyComponent="div"
      bodyClassName={classNames(styles.newShipmentPackage, className)}
      bodyProps={{ 'data-subject': 'new-shipment', 'data-role': 'package' }}
    >
      {children}
    </ConfigurableCard>
  );
}

export function PackageListCardHeader({ index, left, right }) {
  const { styles } = usePackageListCommonStyles();
  return (
    <Row
      align="middle"
      justify="space-between"
      className={classNames(
        'NewShipmentPackage-Header',
        styles.newShipmentPackageHeader,
        'no-margin-form-items'
      )}
    >
      <Col>
        <Row align="middle" gutter={pxToNumber(cssVariables.spaceNorm2)}>
          <Col>
            <div className="number-in-circle">{index + 1}</div>
          </Col>
          {left && <Col>{left}</Col>}
        </Row>
      </Col>
      <Col>{right}</Col>
    </Row>
  );
}

export function PackageListCardTitle({ className, text, textId, ...rest }) {
  const { styles } = usePackageListCommonStyles();
  return (
    <div
      className={classNames(
        'NewShipmentPackage-Title',
        styles.newShipmentPackageTitle,
        className
      )}
      {...rest}
    >
      {textId ? <FormattedMessage id={textId} /> : text}
    </div>
  );
}
export const PackageListCardSubtitle = ({
  className,
  text,
  textId,
  requiredIndicator,
  ...rest
}) => {
  const { styles } = usePackageListCommonStyles();
  return (
    <div
      className={classNames(styles.newShipmentPackageSubtitle, className)}
      {...rest}
    >
      {textId ? <FormattedMessage id={textId} /> : text}
      {requiredIndicator && ' *'}
    </div>
  );
};

export function PackageListCardSection({
  className,
  title,
  titleId,
  children,
  noTopLines,
  mergeWithPrev,
  titleSpaceXxs,
  ...rest
}) {
  const { styles } = usePackageListCommonStyles();
  return (
    <div
      className={classNames(
        styles.newShipmentPackageSection,
        mergeWithPrev && styles.newShipmentPackageSectionMergeWithPrev,
        titleSpaceXxs && styles.newShipmentPackageSectionTitleSpaceXxs,
        { 'has-two-lines': !noTopLines },
        className
      )}
      {...rest}
    >
      {!noTopLines && <TwoLines className="size-10 hide-lg-and-bigger" />}
      {(title || titleId) && (
        <PackageListCardTitle text={title} textId={titleId} />
      )}
      <div
        className={classNames(
          styles.newShipmentPackageSectionBody,
          'no-margin-form-items'
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function PackageListCardSubsection({
  className,
  title,
  titleId,
  headerExtra,
  children,
}) {
  const { styles } = usePackageListCommonStyles();
  return (
    <div className={styles.newShipmentPackageSubsectionParent}>
      <TwoLinesSeparator className="hide-lg-and-bigger" />
      <div
        className={classNames(styles.newShipmentPackageSubsection, className)}
      >
        <Row align="top" justify="space-between">
          <Col className={styles.newShipmentPackageSubsectionTitle}>
            {titleId ? <FormattedMessage id={titleId} /> : title}
          </Col>
          <Col>{headerExtra}</Col>
        </Row>
        {children}
      </div>
    </div>
  );
}

export function canComputeDimensionalWeight(pkg) {
  return (
    pkg &&
    every([pkg.actualWeight, pkg.height, pkg.length, pkg.width], isNumber)
  );
}

export function useComputeDimensionalWeight() {
  const client = useApolloClient();
  const { unitSystem } = useContext(SettingsContext);

  return useCallback(
    async input => {
      const { data } = await client.query({
        query: DIMENSIONAL_WEIGHT_QUERY,
        variables: { input: { ...input, unitSystem } },
      });
      return extractGraphqlEntity(data);
    },
    [client, unitSystem]
  );
}

export function useIsFirstPackageDimensional() {
  const firstPackage = useDynamicOriginalValueDependency(
    'packageList.packages[0]'
  );
  return every(
    [
      firstPackage?.actualWeight,
      firstPackage?.height,
      firstPackage?.length,
      firstPackage?.width,
    ],
    isNumber
  );
}

export function isPackoutDateTimeEnabled(packageSource) {
  return packageSource?.code === 'C'; // Customer / Shipper
}
